/**
 * Project Name: Brainsie eCommerce v4
 * Component: Products Provider Context
 * Description: Centralized API management for product-related dynamic sections
 * Author: Brainsie Team
 * Date Created: November 1, 2025
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { postRecord } from "services/services";

const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error("useProducts must be used within ProductsProvider");
  }
  return context;
};

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const fetchAllProducts = async () => {
    let isMounted = true;

    try {
      // console.log("🔄 ProductsProvider: Fetching products...");

      const response = await postRecord("products/getallwithfilters", {
        Product_Category_Id: 0,
        Product_Sub_Category_Id: 0,
        Category_Name: "",
        Sub_Category_Name: "",
        Tag_Name: "",
        Sort_Column: "Modified_On",
        Sort_Type: "desc",
        Page_Index: 1,
        Records_Per_Page: 50,
        Is_Published: "1",
        Is_Deleted: "0",
        Is_Featured: "0",
        On_Homepage: "0",
        On_Mainmenu: "0",
        Is_Addon: "0",
      });

      if (isMounted && response.status === 200 && response.data) {
        // Transform the data to match the expected format (same as Home-old.jsx)
        const transformedProducts = response.data.products.map((product) => ({
          ...product,
          image:
            product.default_media && product.default_media.length > 0
              ? product.default_media[0]
              : null,
          price: product.default_inventory
            ? product.default_inventory.Total_Selling_Price
            : 0,
          combinationText: product.default_combination
            ? product.default_combination.Combination
            : "",
          averageRating: product.average_review || 0,
          reviewCount: 0,
          Default_Media: product.default_media || [],
          Default_Inventory: product.default_inventory,
          Default_Combination: product.default_combination,
        }));

        // console.log(
        //   "✅ ProductsProvider: Fetched products:",
        //   transformedProducts.length
        // );
        setProducts(transformedProducts);
        setLoading(false);
      }
    } catch (error) {
      if (isMounted) {
        console.error("❌ ProductsProvider: Error fetching products:", error);
        setError(error);
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  };

  // Filtered data getters (same filtering logic as Home-old.jsx)
  const getFeaturedProducts = () => {
    const filtered = products.filter((product) => product.Is_Featured === "1");
    // console.log("🎯 ProductsProvider: Featured products:", filtered.length);
    return filtered;
  };

  const getBestSellerProducts = () => {
    const filtered = products.filter(
      (product) =>
        product.Product_Tags &&
        product.Product_Tags.toLowerCase().includes("best seller")
    );
    // console.log("🎯 ProductsProvider: Best seller products:", filtered.length);
    return filtered;
  };

  const getPopularProducts = () => {
    const filtered = products.filter((product) => product.On_Homepage === "1");
    // console.log("🎯 ProductsProvider: Popular products:", filtered.length);
    return filtered;
  };

  const value = {
    products,
    loading,
    error,
    getFeaturedProducts,
    getBestSellerProducts,
    getPopularProducts,
    refetch: fetchAllProducts,
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};

export default ProductsProvider;
